import { useEffect, useState } from 'react'
import { getMyCertificates, getCertificateVerifyUrl, type Certificate } from '../../lib/api'

/** LinkedIn's documented "Add to Profile" deep link for certifications. */
function getLinkedInAddUrl(cert: Certificate): string {
  const params = new URLSearchParams({
    startTask: 'CERTIFICATION_NAME',
    name: cert.course_title,
    organizationName: 'SateLab',
    certUrl: getCertificateVerifyUrl(cert.certificateCode),
    certId: cert.certificateCode,
    issueYear: String(new Date(cert.issuedAt).getFullYear()),
    issueMonth: String(new Date(cert.issuedAt).getMonth() + 1),
  })
  return `https://www.linkedin.com/profile/add?${params.toString()}`
}

export function CertificatesSection() {
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)
  const [copyErrorCode, setCopyErrorCode] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    getMyCertificates()
      .then((result) => {
        if (!cancelled) setCertificates(result)
      })
      .catch(() => {
        // Best-effort — the section still renders an empty state.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  function handleCopy(code: string) {
    navigator.clipboard
      .writeText(getCertificateVerifyUrl(code))
      .then(() => {
        setCopyErrorCode(null)
        setCopiedCode(code)
        setTimeout(() => setCopiedCode((current) => (current === code ? null : current)), 2000)
      })
      .catch(() => {
        setCopyErrorCode(code)
        setTimeout(() => setCopyErrorCode((current) => (current === code ? null : current)), 2000)
      })
  }

  return (
    <section>
      <p className="account-section__eyebrow">SateLab</p>
      <h1 className="account-section__title">Certificados</h1>
      <p className="account-section__subtitle">Tus certificados de finalización de cursos en Academy.</p>

      <div className="account-section__body">
        {isLoading && <p className="account-status">Cargando certificados…</p>}

        {!isLoading && certificates.length === 0 && (
          <div className="account-empty">Aún no tienes certificados.</div>
        )}

        {!isLoading && certificates.length > 0 && (
          <div className="account-list">
            {certificates.map((cert) => (
              <div key={cert.id} className="account-card">
                <p className="account-card__title">{cert.course_title}</p>
                <p className="account-card__meta">
                  {cert.category} · Emitido el {new Date(cert.issuedAt).toLocaleDateString('es-CO')} · Código {cert.certificateCode}
                </p>
                <div className="account-card__actions">
                  <a
                    href={getCertificateVerifyUrl(cert.certificateCode)}
                    target="_blank"
                    rel="noreferrer"
                    className="account-link-button"
                  >
                    Ver verificación
                  </a>
                  <button type="button" className="account-link-button" onClick={() => handleCopy(cert.certificateCode)}>
                    {copiedCode === cert.certificateCode
                      ? 'Enlace copiado'
                      : copyErrorCode === cert.certificateCode
                        ? 'No se pudo copiar'
                        : 'Copiar enlace'}
                  </button>
                </div>
                <a href={getLinkedInAddUrl(cert)} target="_blank" rel="noreferrer" className="account-linkedin-button">
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
                  </svg>
                  Agregar a LinkedIn
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
